// src/pages/GetStarted.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import LandingPageNav from "../../components/landingpage/LandingPageNav";
import Footer from "../../components/Footer/footer";
import { apigetPublic, apipostPublic } from "../../service/api";

const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
  "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
  "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
  "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

const WOULD_LIKE_TO = [
  "Buy a home",
  "Refinance",
  "Get cash out",
  "Learn about loan options",
  "Talk to a loan officer",
];

const HEAR_ABOUT_JET = [
  "Realtor",
  "Builder / Community",
  "Friend / Family",
  "Google Search",
  "Social Media",
  "Radio / TV",
  "Email",
  "Other",
];

function normalizeEndpoint(endpoint) {
  // Accepts "lead/add" or "/lead/add"
  if (!endpoint) return "";
  return endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
}

function buildRapport(formData, extras = {}) {
  // “Rapport” = everything that doesn’t belong to a defined category.
  // We’ll treat the “additional details” textarea + any optional fields as rapport lines.
  const lines = [];

  if (formData.details?.trim()) lines.push(`Additional Details: ${formData.details.trim()}`);
 // if (formData.communityName?.trim()) lines.push(`Community of Interest: ${formData.communityName.trim()}`);
 // if (formData.realtorName?.trim()) lines.push(`Realtor Name: ${formData.realtorName.trim()}`);
  // if (formData.realtorEmail?.trim()) lines.push(`Realtor Email: ${formData.realtorEmail.trim()}`);

  // Extras (page context, source, etc.)
  Object.entries(extras).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const str = String(v).trim();
    if (!str) return;
    lines.push(`${k}: ${str}`);
  });

  return lines.join("\n");
}

export default function GetStartedPage() {
  const [intakeCfg, setIntakeCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    wouldLikeTo: "",
    communityName: "",
    realtorName: "",
    realtorEmail: "",
    details: "",
    hearAboutJet: "",
    
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Pull intake config from the same endpoint your dashboard uses
  useEffect(() => {
    const loadCfg = async () => {
      try {
        const res = await apigetPublic("intake");
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];

        // Prefer a specific slug if you have it in DB (recommended)
        // Create an intake record with slug: "getstarted"
        const cfg =
          list.find((x) => String(x?.slug || "").toLowerCase() === "getstarted") ||
          list.find((x) => String(x?.title || "").toLowerCase().includes("get started")) ||
          list.find((x) => String(x?.slug || "").toLowerCase().includes("get")) ||
          null;

        setIntakeCfg(cfg);
      } catch (e) {
        console.error("Failed to load intake config:", e);
        setIntakeCfg(null);
      } finally {
        setCfgLoading(false);
      }
    };

    loadCfg();
  }, []);

  const requiredMissing = useMemo(() => {
    const required = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "city",
      "state",
      "wouldLikeTo",
      "hearAboutJet",
    ];
    return required.filter((k) => !String(formData[k] ?? "").trim());
  }, [formData]);

  const brandGold = intakeCfg?.brandColor || "#b89b5e";
  const title = intakeCfg?.title || "Ready to Find Your Dream Home?";
  const subtitle = intakeCfg?.subtitle || "LET'S GET STARTED";
  const intro =
    intakeCfg?.intro ||
    "Fill out this form, and one of our loan officers will reach out to guide you through the mortgage process!";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (requiredMissing.length) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fill out all required fields.",
      });
      return;
    }

    if (!intakeCfg?.endpoint) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Form endpoint is not configured. Please contact support.",
      });
      return;
    }

    // ✅ Category mapping:
    // - “Tell Us About You” -> basic contact fields
    // - “Tell Us About Your Needs” -> wouldLikeTo, etc.
    // - Anything else -> rapport
    const payload = {
      // helpful identifiers
      intakeSlug: intakeCfg?.slug || "getstarted",
      intakeTitle: intakeCfg?.title || "Get Started",
        leadSource: intakeCfg?.candidSlug || intakeCfg?.title || "Get Started",
      // Contact / borrower info category
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      emailAddress: formData.email.trim(),
      phoneNumber: formData.phone.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      community : formData.communityName.trim(),
      realtorName : formData.realtorName.trim(),
      realtorEmail : formData.realtorEmail.trim(),
      slug: "getstarted",
      // Needs category
      interestType: formData.wouldLikeTo.trim(),
      referralSource: formData.hearAboutJet.trim(),

      // ✅ Everything that doesn’t cleanly fit goes to rapport
      additionalDetails: buildRapport(formData, {
        "Page": "Get Started",
        "Submitted At": new Date().toISOString(),
        "User Agent": typeof navigator !== "undefined" ? navigator.userAgent : "",
        "URL": typeof window !== "undefined" ? window.location.href : "",
      }),
    };

    try {
      const endpoint = normalizeEndpoint(intakeCfg.endpoint);
      const res = await apipostPublic(endpoint, payload);

      if (res?.status === 201 || res?.data?.message) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Submitted! A loan officer will reach out shortly.",
        });

        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          city: "",
          state: "",
          wouldLikeTo: "",
          communityName: "",
          realtorName: "",
          realtorEmail: "",
          details: "",
          hearAboutJet: "",
        });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      console.error("Get Started submission failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to submit. Please try again.",
      });
    }
  };

  return (
    <>
      <LandingPageNav />
      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />

      <Box sx={{ py: 6 }}>
        <Container maxWidth="md">
          <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 1 }}>
            {/* Top gold bar (like screenshot) */}
            <Box sx={{ height: 56, backgroundColor: brandGold }} />

            <Box sx={{ p: { xs: 3, md: 5 } }}>
              {cfgLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="h4" align="center" sx={{ fontWeight: 600, mb: 1 }}>
                    {title}
                  </Typography>

                  <Typography
                    align="center"
                    sx={{
                      letterSpacing: "0.35em",
                      fontSize: 13,
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {subtitle}
                  </Typography>

                  <Typography variant="body2" align="center" sx={{ color: "text.secondary", mb: 4 }}>
                    {intro}
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    {/* Tell Us About You */}
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Tell Us About You</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="First Name"
                          name="firstName"
                          fullWidth
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Last Name"
                          name="lastName"
                          fullWidth
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          name="email"
                          type="email"
                          fullWidth
                          required
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Phone"
                          name="phone"
                          fullWidth
                          required
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="City"
                          name="city"
                          fullWidth
                          required
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="State"
                          name="state"
                          fullWidth
                          required
                          value={formData.state}
                          onChange={handleChange}
                        >
                          {STATES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>

                    {/* Tell Us About Your Needs */}
                    <Box sx={{ mt: 4 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Tell Us About Your Needs
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="I would like to:"
                            name="wouldLikeTo"
                            fullWidth
                            required
                            value={formData.wouldLikeTo}
                            onChange={handleChange}
                          >
                            {WOULD_LIKE_TO.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Community Name of Interest? (skip if undecided)"
                            name="communityName"
                            fullWidth
                            value={formData.communityName}
                            onChange={handleChange}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Realtor Name (if applicable)"
                            name="realtorName"
                            fullWidth
                            value={formData.realtorName}
                            onChange={handleChange}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Realtor Email"
                            name="realtorEmail"
                            type="email"
                            fullWidth
                            value={formData.realtorEmail}
                            onChange={handleChange}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Any additional details you'd like to share with our team?"
                            name="details"
                            fullWidth
                            multiline
                            minRows={5}
                            value={formData.details}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Before You Go */}
                    <Box sx={{ mt: 4 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Before You Go...
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            select
                            label="How did you hear about Jet?"
                            name="hearAboutJet"
                            fullWidth
                            required
                            value={formData.hearAboutJet}
                            onChange={handleChange}
                          >
                            {HEAR_ABOUT_JET.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{
                              mt: 1,
                              py: 1.3,
                              fontWeight: 700,
                              letterSpacing: 1,
                              backgroundColor: "#3e4b59",
                              "&:hover": { backgroundColor: "#32404c" },
                            }}
                          >
                            SUBMIT
                          </Button>

                          {!intakeCfg?.endpoint ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "error.main" }}>
                              Missing intake endpoint configuration.
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                              Posting to: {intakeCfg.endpoint}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      <Footer />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
